import React from "react";
import { withFormik } from "formik";
import _ from "lodash";
import { Grid } from "@material-ui/core";
import {
  InputText,
  InputTypeAhead,
  LabelDecimal,
  LabelDollarFormatted,
  LabelPercentFormatted,
  PrimaryButton,
  SecondaryButton
} from "@loanbossinc/component-library";

import HousesIcon from "../../../houses.svg";

import {
  ControlRow,
  ControlRowItem,
  DescriptionWrapper,
  FieldDataWrapper,
  FieldNameWrapper,
  HousesIconWrapper,
  InputWrapper,
  LeftSectionWrapper,
  NoiReportNameWrapper,
  PaddedWrapper,
  RightSectionWrapper,
  Row,
  SectionWrapper,
  SelectButtonWrapper,
  StyledSelectButton,
  NoiFieldDataWrapper
} from "./loanBossFieldStyles";

const DEAL_FIELDS = [
  { name: "Current Balance", id: "681d83ea-2e11-11e9-ac70-0242ac140002", format: "money" },
  { name: "Total Loan Amount", id: "d2ae6abe-080b-11eb-867f-f1f1e6aba435", format: "money" },
  { name: "Unfunded Commitments", id: "681d843a-2e11-11e9-ac70-0242ac140002", format: "money" }
];

const PROPERTY_FIELDS = [
  { name: "Land Size (Acres)", id: "landSize", format: "integer" },
  { name: "Occupancy", id: "occupancyAssumptionRaw", format: "percentage" },
  { name: "Property Appraised Value", id: "appraisedValue", format: "money" },
  { name: "Property Market Value", id: "marketValue", format: "money" },
  { name: "Square Feet", id: "squareFeet", format: "integer" },
  { name: "Units", id: "units", format: "integer" }
];

const formatValues = (value, format) => {
  switch (format) {
    case "money":
      return <LabelDollarFormatted value={value} decimals={2} />;
    case "percentage":
      return <LabelPercentFormatted value={value} decimals={2} />;
    case "integer":
      return <LabelDecimal value={value} />;
    default:
      return value;
  }
};

const LoanBossField = props => {
  const {
    deal,
    handleSubmit,
    setFieldValue,
    setFieldSelections,
    values,
    noiValuesPerReportGroup,
    selectedField,
    isEditing,
    isEditingLineItem,
    setDrawerSelections
  } = props;

  const { name = "", loanBossField = null, propertyId = null } = values;

  const onCancel = () => {
    setFieldSelections({ currentSelectedFieldType: null, isAdding: false, isEditing: false, selectedField: null });
    if (isEditingLineItem) {
      setDrawerSelections({ isEditingLineItem: true, currentSectionName: "line-item-management" });
    }
  };

  const onNameChange = event => {
    const {
      target: { value }
    } = event;

    setFieldValue("name", value);
  };

  const setSelectedPropertyId = value => {
    setFieldValue("propertyId", value);

    if (value !== -1) {
      setFieldValue("level", "property");
    } else {
      setFieldValue("level", "deal");
    }
  };

  const properties = [{ id: -1, name: "Deal" }];
  _.sortBy(deal.properties, ["name"]).forEach(x => properties.push({ id: x.id, name: x.name }));

  const selectedProperty = Boolean(propertyId) && propertyId !== -1 && deal.properties.find(x => x.id === propertyId);

  const canSave = Boolean(name) && Boolean(propertyId) && (Boolean(loanBossField) || Boolean(selectedField?.reportGroupId));

  const onFieldSelected = field => {
    setFieldValue("loanBossField", field.id);
  };

  const onFieldNoiSelected = (reportGroup) => {
    setFieldValue("loanBossField", reportGroup);
  };

  const filterNoiFields = (reportGroup) => {
    if (selectedField) {
      return reportGroup.reportGroupId === selectedField.reportGroupId && reportGroup.type === selectedField.noiType;
    } else {
      if (loanBossField) {
        return reportGroup.reportGroupId === loanBossField.reportGroupId && reportGroup.type === loanBossField.type;
      }
    }
    return true;
  };

  return (
    <Grid id="loan-boss-field">
      <PaddedWrapper padding="1em">
        <InputText id="field-name-input" variant="filled" label="Field Name" name="field-name" fullWidth onChange={onNameChange} value={name} />
      </PaddedWrapper>

      <SectionWrapper>
        <LeftSectionWrapper>
          <Grid container justify="flex-start" alignItems="center" direction="row">
            <Grid item xs={2}>
              <HousesIconWrapper>
                <img src={HousesIcon} alt="Houses Icon" height={40} width={32} />
              </HousesIconWrapper>
            </Grid>
            <Grid item xs={10}>
              <DescriptionWrapper id="dscr">Show metrics for the deal or a specific property.</DescriptionWrapper>
            </Grid>
          </Grid>
        </LeftSectionWrapper>
        <RightSectionWrapper>
          <InputWrapper id="level-dd">
            <ControlRow>
              <ControlRowItem>
                <InputTypeAhead
                  variant="filled"
                  id="selectedPropertyId"
                  name="Property"
                  label="Select"
                  options={properties}
                  displayWith={(option) => option && option.name}
                  setValueWith={(option) => option && option.id}
                  onChange={setSelectedPropertyId}
                  value={propertyId}
                  disabled={isEditing}
                />
              </ControlRowItem>
            </ControlRow>
          </InputWrapper>
        </RightSectionWrapper>
      </SectionWrapper>

      {Boolean(propertyId) &&
        propertyId === -1 &&
        !selectedField?.reportGroupId &&
        DEAL_FIELDS.filter((x) => !loanBossField || loanBossField === x.id).map((y) => (
          <Row key={y.id} justifyContent="flex-start" showSelect={!values.loanBossField}>
            <PaddedWrapper>
              <FieldNameWrapper>{y.name}</FieldNameWrapper>
            </PaddedWrapper>
            <FieldDataWrapper>{formatValues(deal[y.id]?.decimalValue || deal[y.id]?.integerValue, y.format)}</FieldDataWrapper>
            <SelectButtonWrapper>
              <StyledSelectButton id="select-metrics-btn" label="Select" onClick={() => onFieldSelected(y)} />
            </SelectButtonWrapper>
          </Row>
        ))}

      {Boolean(propertyId) &&
        propertyId !== -1 &&
        !selectedField?.reportGroupId &&
        PROPERTY_FIELDS.filter((x) => !loanBossField || loanBossField === x.id).map((y) => (
          <Row key={y.id} justifyContent="flex-start" showSelect={!values.loanBossField}>
            <FieldNameWrapper>
              <PaddedWrapper>{y.name}</PaddedWrapper>
            </FieldNameWrapper>
            <FieldDataWrapper>{formatValues(selectedProperty[y.id], y.format)}</FieldDataWrapper>
            {!loanBossField && (
              <SelectButtonWrapper>
                <StyledSelectButton label="Select" onClick={() => onFieldSelected(y)} />
              </SelectButtonWrapper>
            )}
          </Row>
        ))}

      {Boolean(propertyId) &&
        propertyId === -1 &&
        noiValuesPerReportGroup &&
        noiValuesPerReportGroup
          .filter(filterNoiFields)
          .map((reportGroup) => {
            return (
              <Row key={`${reportGroup.reportGroupId}-${reportGroup.type}`} justifyContent="flex-start" showSelect={!selectedField && !loanBossField}>
                <FieldNameWrapper>
                  <PaddedWrapper padding="0 2.2em">{`ADJUSTED ${reportGroup.type === "income" ? "INCOME" : "EXPENSES"} TOTAL`}</PaddedWrapper>
                </FieldNameWrapper>
                <NoiFieldDataWrapper>
                  <FieldDataWrapper>{formatValues(reportGroup.value, "money")}</FieldDataWrapper>
                  <NoiReportNameWrapper>{reportGroup.reportName}</NoiReportNameWrapper>
                </NoiFieldDataWrapper>
                {(!selectedField && !loanBossField) && (
                  <SelectButtonWrapper>
                    <StyledSelectButton label="Select" onClick={() => onFieldNoiSelected(reportGroup)} />
                  </SelectButtonWrapper>
                )}
              </Row>
            );
          })
      }

      <Row borderBottom="none" justifyContent="flex-end" background="none" id="button-group">
        <PaddedWrapper>
          <Grid container>
            <Grid item>
              <SecondaryButton size="sm" label="Cancel" onClick={onCancel} />
            </Grid>
            <Grid item>
              <PaddedWrapper padding="0 1em">
                <PrimaryButton id="confirm-btn" disabled={!canSave} size="sm" label="Save" onClick={handleSubmit} />
              </PaddedWrapper>
            </Grid>
          </Grid>
        </PaddedWrapper>
      </Row>
    </Grid>
  );
};

export default withFormik({
  mapPropsToValues: ({ selectedField }) => ({ ...selectedField, rowsSelected: selectedField?.rowsSelected ?? [] }),
  handleSubmit(values, { props, setSubmitting }) {
    const {
      dealId,
      isAdding,
      postFieldConfig,
      putFieldConfig,
      setFieldSelections,
      changeDrawerStateIfInNewListItemMode,
      deal,
      isEditingLineItem,
      setNoiCalculationsDashboardSelections,
      setDrawerSelections,
      setNewLineItemsSelectionsToDefault
    } = props;

    const payload = {
      ...values,
      dealId,
      isFromLoanBoss: true,
      type: "loanboss"
    };

    if (isAdding) {
      let value = 0;
      if (values.level === "deal") {
        if (values.loanBossField?.reportGroupId) {
          value = values.loanBossField.value;
        } else {
          const selectedFormulaField = deal[values.loanBossField];
          value = selectedFormulaField.decimalValue || selectedFormulaField.integerValue;
        }
      } else {
        const property = deal.properties.find((x) => x.id === values.propertyId);
        value = property[values.loanBossField];
      }

      payload.value = value;
      payload.formattedValue = `${value}`;

      if (values.loanBossField?.reportGroupId) {
        const { reportGroupId, type } = values.loanBossField;
        payload.reportGroupId = reportGroupId;
        payload.noiType = type;
        payload.loanBossField = null;
      }
    }

    if (isAdding) {
      postFieldConfig({ data: payload });
    } else {
      putFieldConfig({ data: _.omit(payload, ["updatedAt", "createdAt"]) });
    }

    setSubmitting(false);
    changeDrawerStateIfInNewListItemMode();
    setFieldSelections({ currentSelectedFieldType: null, selectedField: null, isAdding: false, isEditing: false });
    if (isEditingLineItem) {
      setNoiCalculationsDashboardSelections({
        initialDrawerSectionName: null,
        isLenderReportingDrawerOpen: false,
        addLineItemType: null,
        lineItemToEdit: null
      });
      setDrawerSelections({ isEditingLineItem: false, currentSectionName: null });
      setNewLineItemsSelectionsToDefault();
    }
  }
})(LoanBossField);
